import { apiClient } from './client'
import type {
  CreateMoveConnectionDto,
  MoveConnectionResponseDto,
} from './generated/api'

/**
 * Move Connections API Facade
 *
 * Type-safe wrapper around the generated API client for move connections.
 * Handles directional flow connections between moves within a series.
 */

export type { CreateMoveConnectionDto, MoveConnectionResponseDto }

/**
 * Fetch all connections for a specific series
 *
 * @param seriesId - Series ID (UUID)
 * @param includeRelations - Include related move and series data
 * @returns Promise resolving to array of connections
 */
export async function fetchSeriesConnections(
  seriesId: string,
  includeRelations?: boolean
): Promise<MoveConnectionResponseDto[]> {
  const response =
    await apiClient.api.moveConnectionsControllerGetSeriesConnections(
      seriesId,
      { includeRelations }
    )
  return response.data
}

/**
 * Create a new move connection
 *
 * @param data - Connection data (seriesId, fromMoveId, toMoveId)
 * @returns Promise resolving to created connection
 */
export async function createConnection(
  data: CreateMoveConnectionDto
): Promise<MoveConnectionResponseDto> {
  const response = await apiClient.api.moveConnectionsControllerCreate(data)
  return response.data
}

/**
 * Delete a connection by ID (soft delete)
 *
 * @param id - Connection ID (UUID)
 * @returns Promise resolving to void on success
 */
export async function deleteConnection(id: string): Promise<void> {
  await apiClient.api.moveConnectionsControllerDelete(id)
}

/**
 * Remove a specific connection by series and move IDs
 *
 * @param seriesId - Series ID (UUID)
 * @param fromMoveId - From move ID (UUID)
 * @param toMoveId - To move ID (UUID)
 * @returns Promise resolving to void on success
 */
export async function removeConnection(
  seriesId: string,
  fromMoveId: string,
  toMoveId: string
): Promise<void> {
  await apiClient.api.moveConnectionsControllerRemoveConnection(
    seriesId,
    fromMoveId,
    toMoveId
  )
}
